import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  Grid,
  InputAdornment, 
  IconButton,
  CircularProgress, // Added comma
  Divider           // Added Divider import
} from '@mui/material';
import { Visibility, VisibilityOff, Google as GoogleIcon } from '@mui/icons-material';
import MicrosoftIcon from '@mui/icons-material/Window';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { googleProvider, microsoftProvider } from '../../firebase';
import { COLORS, FONTS } from '../../theme/tokens';
import {
  FORM_FIELD_SX,
  PANEL_SX,
  PRIMARY_BUTTON_SX,
  QUIET_LINK_SX,
  SECONDARY_BUTTON_SX,
} from '../../theme/v2Styles';
const Register = ({ switchToLogin }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstname: '',
    lastname: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const { email, password, confirmPassword, firstname, lastname } = formData;
  // Use the custom hook
  const { register, loginWithFirebase, isAuthenticated, error, clearErrors, loading } = useAuth();
  const navigate = useNavigate();

  // Effect for clearing errors on mount/unmount
  useEffect(() => {
    clearErrors();
    return () => {
      clearErrors();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once

  // Effect for handling redirection after authentication
  useEffect(() => {
    if (isAuthenticated && !loading) {
      //console.log("Register component detected authenticated state, redirecting...");
      const redirectUrl = localStorage.getItem('redirectAfterLogin');
      localStorage.removeItem('redirectAfterLogin'); // Clear it
      navigate(redirectUrl || '/dashboard', { replace: true });
    }
    // Dependencies: trigger when auth state or loading finishes
  }, [isAuthenticated, loading, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Clear field-specific error when user starts typing
    if (formErrors[e.target.name]) {
      setFormErrors({ ...formErrors, [e.target.name]: null });
    }
  };
  const validateForm = () => {
    const errors = {};
    if (!firstname.trim()) {
      errors.firstname = t('auth.errFirstName');
    }
    if (!lastname.trim()) {
      errors.lastname = t('auth.errLastName');
    }
    if (!email) {
      errors.email = t('auth.errEmailRequired');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = t('auth.errEmailInvalid');
    }
    if (!password) {
      errors.password = t('auth.errPasswordRequired');
    } else if (password.length < 6) {
      errors.password = t('auth.errPasswordShort');
    }
    if (password !== confirmPassword) {
      errors.confirmPassword = t('auth.errPasswordsMismatch');
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Remove confirmPassword before sending to API
      const { confirmPassword, ...registrationData } = formData;
      try {
        await register(registrationData);
      } catch (err) {}
    }
  };
  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const toggleShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  const handleFirebaseLogin = async (provider) => {
    // Clear previous errors before attempting social login
    clearErrors();
    const success = await loginWithFirebase(provider);
  };

  return (
    <Box
      data-testid="register-v2-layout"
      sx={{
        ...PANEL_SX,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' },
        maxWidth: 1080,
        mx: 'auto',
        my: { xs: 1, md: 3 },
        overflow: 'hidden',
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'center',
          p: 7,
          bgcolor: COLORS.surfaceWarm,
        }}
      >
        <Typography sx={{ color: COLORS.goldText, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', mb: 2 }}>
          {t('auth.memberBenefits')}
        </Typography>
        <Typography sx={{ fontFamily: FONTS.heading, fontSize: '1.875rem', fontWeight: 600, lineHeight: 1.25, color: COLORS.textPrimary, mb: 3.5 }}>
          {t('auth.benefitsTagline')}
        </Typography>
        {[
          [t('auth.benefitOrderTracking'), t('auth.benefitOrderTrackingText')],
          [t('auth.benefitWishlist'), t('auth.benefitWishlistText')],
          [t('auth.benefitCheckout'), t('auth.benefitCheckoutText')],
        ].map(([title, description]) => (
          <Box key={title} sx={{ borderTop: `1px solid ${COLORS.dividerWarm}`, pt: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 700, color: COLORS.textPrimary }}>{title}</Typography>
            <Typography sx={{ mt: 0.5, color: COLORS.textBody, fontSize: '0.875rem', lineHeight: 1.55 }}>{description}</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ px: { xs: 3, sm: 5, md: 7 }, py: { xs: 4, md: 6 } }}>
        <Typography component="h1" sx={{ fontFamily: FONTS.heading, fontWeight: 600, fontSize: { xs: '1.625rem', md: '1.875rem' }, color: COLORS.textPrimary, mb: 1 }}>
          {t('auth.createAccount')}
        </Typography>
        <Typography sx={{ color: COLORS.textSecondary, mb: 3.5 }}>{t('auth.takesAMinute')}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('auth.email')}
              type="email"
              name="email"
              value={email}
              onChange={handleChange}
              fullWidth
              margin="normal"
              error={!!formErrors.email}
              helperText={formErrors.email}
              required
              autoComplete="email"
              sx={FORM_FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('auth.firstName')}
              name="firstname"
              value={firstname}
              onChange={handleChange}
              fullWidth
              margin="normal"
              error={!!formErrors.firstname}
              helperText={formErrors.firstname}
              autoComplete="given-name"
              required
              sx={FORM_FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('auth.lastName')}
              name="lastname"
              value={lastname}
              onChange={handleChange}
              fullWidth
              margin="normal"
              error={!!formErrors.lastname}
              helperText={formErrors.lastname}
              autoComplete="family-name"
              required
              sx={FORM_FIELD_SX}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('auth.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={password}
              onChange={handleChange}
              fullWidth
              margin="normal"
              error={!!formErrors.password}
              helperText={formErrors.password}
              required
              sx={FORM_FIELD_SX}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShowPassword}
                      edge="end"
                      aria-label={t('auth.togglePassword')}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('auth.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={confirmPassword}
              onChange={handleChange}
              fullWidth
              margin="normal"
              error={!!formErrors.confirmPassword}
              helperText={formErrors.confirmPassword}
              required
              sx={FORM_FIELD_SX}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={toggleShowConfirmPassword}
                      edge="end"
                      aria-label={t('auth.toggleConfirmPassword')}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Grid>
        </Grid>
        <Button 
          type="submit" 
          variant="contained" 
          color="primary" 
          fullWidth 
          sx={{ ...PRIMARY_BUTTON_SX, mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : t('auth.register')}
        </Button>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="body2">
            {t('auth.haveAccount')}{' '}
            <Button 
              onClick={switchToLogin} 
              color="primary" 
              sx={{ ...QUIET_LINK_SX, textTransform: 'none', p: 0, ml: 1 }}
            >
              {t('auth.loginHere')}
            </Button>
          </Typography>
        </Box>
      </Box>

      {/* Divider and Social Logins */}
        <Divider sx={{ my: 3, color: COLORS.textFaint, fontSize: '0.6875rem', letterSpacing: '1px' }}>{t('auth.or')}</Divider>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
        <Button
          variant="outlined"
          startIcon={<GoogleIcon />}
          fullWidth
          onClick={() => handleFirebaseLogin(googleProvider)}
          disabled={loading}
          sx={{ ...SECONDARY_BUTTON_SX, justifyContent: 'center', color: COLORS.textPrimary, borderColor: COLORS.borderControl }}
        >
          {t('auth.signUpGoogle')}
        </Button>
        {/* Facebook signup temporarily hidden - uncomment when Facebook OAuth is fixed
        <Button
          variant="outlined"
          startIcon={<FacebookIcon />}
          fullWidth
          onClick={() => handleFirebaseLogin(facebookProvider)}
          disabled={loading}
          sx={{ 
            justifyContent: 'center',
            color: '#1877F2', // Facebook blue
            borderColor: '#1877F2',
            '&:hover': {
              borderColor: '#1877F2',
              backgroundColor: 'rgba(24, 119, 242, 0.04)'
            }
          }}
        >
          {t('auth.signUpFacebook')}
        </Button> */}
        <Button
          variant="outlined"
          startIcon={<MicrosoftIcon />}
          fullWidth
          onClick={() => handleFirebaseLogin(microsoftProvider)}
          disabled={loading}
          sx={{ ...SECONDARY_BUTTON_SX, justifyContent: 'center', color: COLORS.textPrimary, borderColor: COLORS.borderControl }}
        >
          {t('auth.signUpMicrosoft')}
        </Button>
        {/* Apple Sign up Button Removed */}
        </Box>
      </Box>
    </Box>
  );
};
export default Register;
